import type { AriaAttributes, JSX, ReactElement, Ref } from 'react';

import { useDebounce } from 'hooks';
import type { HTMLAttributes, HTMLConstructors } from 'types';
import { cn } from 'utils/classNames';

// Allow only the tags in actual use (add more as needed). The union of tags in
// combination with the ref balloons the type checking time if all HTML tags
// are included.
type AsTagName = 'div' | 'p' | 'span';

interface Props extends Omit<
	HTMLAttributes<HTMLConstructors[AsTagName]>,
	'aria-atomic' | 'aria-live' | 'aria-relevant'
> {
	as: AsTagName;

	/**
	 * If the entire text should be read again even if only parts are updated.
	 * Set if the updated text doesn't make sense on its own. Something like
	 * "X search results" where only X is changed when searching will only have
	 * the number announced without atomic: "23 search results... 18... 31..."
	 */
	atomic?: boolean;

	/** Delay in milliseconds for updates to the text. */
	debounceDelay?: number;

	/** If the text is only visible to screen readers */
	hidden?: boolean;

	/**
	 * The aggressiveness level, defaults to polite. Only use assertive for
	 * critical or time-sensitive notifications that requires the user's
	 * immediate attention, since they interrupt any announcement a screen
	 * reader is currently making.
	 */
	level?: 'assertive' | 'polite';

	ref?: Ref<HTMLConstructors[AsTagName]>;

	/** What modifications to announce, defaults to 'additions text'. */
	relevant?: AriaAttributes['aria-relevant'];

	/** The text to announce. */
	text: string | ReactElement | JSX.Element | JSX.Element[] | undefined;
}

/**
 * Text that's announced to screen readers when updated.
 *
 * IMPORTANT! The component should always be rendered even when there is no
 * text, otherwise the text may not be announced when it's added.
 *
 * @example
 *
 *     const text: string | undefined;
 *
 *     // Incorrect
 *     {text && <ScreenReaderAnnouncementText />}
 *
 *     // Correct
 *     <ScreenReaderAnnouncementText text={text} />
 */
export default function ScreenReaderAnnouncementText({
	as: Tag,
	atomic,
	className,
	debounceDelay,
	hidden,
	level = 'polite',
	ref,
	relevant,
	text = '',
	...attrs
}: Props) {
	const debouncedText = useDebounce(text, debounceDelay);
	const content = debounceDelay ? debouncedText : text;

	return (
		<Tag
			{...attrs}
			// @ts-expect-error Complains that `align` is required for div and
			// missing for span.
			ref={ref}
			aria-live={level}
			aria-atomic={atomic ? true : undefined}
			aria-relevant={relevant}
			className={cn(className, hidden && 'sr-only')}
		>
			{content}
		</Tag>
	);
}
ScreenReaderAnnouncementText.displayName = 'ScreenReaderAnnouncementText';
